import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, CheckCircle2, HardHat, FileSignature, Star, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ISSUE_STATUS, ORDER_APPT_KINDS, ORDER_TONE, fmtDateTime, fmtDay, fmtEuro } from "@/lib/format";
import { Btn, Chip, Empty, PageHeader, Pill, ProgressBar, Spinner } from "@/components/rp";
import { DocItem, Info, Section, Timeline } from "@/components/commesse";
import { CantiereWork } from "@/components/CantiereWork";
import { ClientPratica } from "@/components/ClientPratica";
import { Diario } from "@/components/Diario";
import { SignaturePad } from "@/components/SignaturePad";
import { ClientLavori } from "@/components/ClientLavori";
import { ClientSopralluogo } from "@/components/ClientSopralluogo";
import { ClientPercorso } from "@/components/admin/percorso";
import { ClientPrima } from "@/components/admin/primaDopo";
import { ClientPostCantiere, VerbalePreview, useClientChiusura } from "@/components/ClientChiusura";

const Rating = ({ label, value, onChange, id }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-sm">{label}</span>
    <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`rating-${id}-${n}`}><Star className={`h-6 w-6 ${n <= value ? "fill-black text-black" : "text-[#C8BDAB]"}`} /></button>
    ))}</div>
  </div>
);

const SignBox = ({ title, text, button, onSign, testid }) => {
  const [ok, setOk] = useState(false);
  const [name, setName] = useState("");
  return (
    <Section label={title} className="bg-[#E6DDCE]/40 border-[#C8BDAB]" testid={testid}>
      <label className="flex gap-3 text-sm items-start cursor-pointer"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-black" data-testid={`${testid}-checkbox`} />{text}</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="rp-field mt-4 bg-white" placeholder="Nome e cognome (firma)" data-testid={`${testid}-name`} />
      <Btn className="mt-4" disabled={!ok || name.trim().length < 3} onClick={() => onSign(name)} data-testid={`${testid}-button`}><FileSignature className="h-4 w-4" /> {button}</Btn>
    </Section>
  );
};

const DeliveryBox = ({ act, oid }) => {
  const [r, setR] = useState({ rating_product: 5, rating_seller: 5, rating_team: 5, rating_materials: 5, note: "", has_problem: false, name: "", signature: null });
  const [c] = useClientChiusura(oid);
  return (
    <Section label="Firma il verbale di fine lavori" className="bg-[#E6DDCE]/40 border-[#C8BDAB]" testid="client-delivery-box">
      {c && <VerbalePreview c={c} oid={oid} />}
      <p className="rp-label mb-2">La tua valutazione</p>
      <div className="space-y-3">
        <Rating label="Imprese" value={r.rating_team} onChange={(v) => setR({ ...r, rating_team: v })} id="team" />
        <Rating label="Servizio" value={r.rating_seller} onChange={(v) => setR({ ...r, rating_seller: v })} id="seller" />
        <Rating label="Materiali" value={r.rating_materials} onChange={(v) => setR({ ...r, rating_materials: v })} id="materials" />
        <Rating label="Progetto" value={r.rating_product} onChange={(v) => setR({ ...r, rating_product: v })} id="product" />
      </div>
      <div className="flex gap-2 mt-4">
        <Chip active={!r.has_problem} onClick={() => setR({ ...r, has_problem: false })} data-testid="delivery-no-problem">Tutto in ordine</Chip>
        <Chip active={r.has_problem} onClick={() => setR({ ...r, has_problem: true })} data-testid="delivery-has-problem">C'è un problema</Chip>
      </div>
      <textarea rows={2} value={r.note} onChange={(e) => setR({ ...r, note: e.target.value })} className="rp-field mt-3 bg-white" placeholder={r.has_problem ? "Descrivi il problema" : "Vuoi lasciarci un commento?"} data-testid="delivery-note" />
      <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} className="rp-field mt-3 bg-white" placeholder="Nome e cognome (firma)" data-testid="delivery-name" />
      <div className="mt-3"><SignaturePad onChange={(signature) => setR((x) => ({ ...x, signature }))} testid="delivery-signature" /></div>
      <Btn className="mt-4" disabled={r.name.trim().length < 3 || !r.signature} onClick={() => act("confirm-delivery", r)} data-testid="delivery-sign-button"><FileSignature className="h-4 w-4" /> Firma il verbale di fine lavori</Btn>
    </Section>
  );
};

const IssueBox = ({ o, act }) => {
  const [text, setText] = useState("");
  return (
    <Section label="Assistenza" testid="client-issues">
      {o.issues.map((i) => (
        <div key={i.id} className="rounded-2xl bg-white border border-[#EBEBEB] p-3 mb-2 text-sm">
          <p>{i.description}</p>
          <p className="text-xs mt-1"><Pill tone={i.status === "risolta" ? "success" : "warning"}>{ISSUE_STATUS[i.status]}</Pill> <span className="text-[#757575] ml-1">{i.status_label}{i.public_message && ` · ${i.public_message}`}</span></p>
        </div>
      ))}
      <div className="flex gap-2 mt-2"><input value={text} onChange={(e) => setText(e.target.value)} className="rp-field bg-white" placeholder="Segnala un problema" data-testid="client-issue-input" /><Btn variant="secondary" disabled={!text.trim()} onClick={() => { act("issues", { description: text }); setText(""); }} data-testid="client-issue-send">Invia</Btn></div>
    </Section>
  );
};

const OrderView = ({ o, setO }) => {
  const act = async (path, body = {}) => {
    try { setO((await api.post(`/client/orders/${o.id}/${path}`, body)).data); toast.success("Fatto, grazie!"); } catch (e) { toast.error(errMsg(e)); }
  };
  const project = o.documents.filter((d) => d.tag === "progetto");
  const appts = o.appointments.filter((a) => a.date >= new Date().toLocaleDateString("en-CA") && a.status !== "completato");
  return (
    <div className="space-y-6">
      <div className="rp-card p-6 sm:p-8 rp-fade">
        <p className="text-xs font-mono text-[#757575]">{o.code}</p>
        <h2 className="text-3xl font-semibold tracking-tight mt-1" data-testid="client-order-title">{o.title}</h2>
        <p className="text-sm text-[#757575] mt-1">{o.delivery_address}</p>
        <div className="flex justify-between items-end mt-6 mb-2"><Pill tone={ORDER_TONE(o.status)} data-testid="client-order-status">{o.status_label}</Pill><span className="text-sm font-semibold">{o.steps_done} di {o.steps_total} fasi</span></div>
        <ProgressBar value={(o.steps_done / o.steps_total) * 100} />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6" data-testid="client-order-info">
          <Info label="Importo contratto">{fmtEuro(o.contract_amount)}</Info>
          <Info label="Ancora da pagare">{fmtEuro(o.remaining_amount)}</Info>
          <Info label="Arrivo materiali">{o.actual_arrival_date ? `Arrivati il ${fmtDay(o.actual_arrival_date)}` : o.expected_arrival_date ? `Previsto ${fmtDay(o.expected_arrival_date)}` : "Da definire"}</Info>
          <Info label="Referente">{o.seller_name}</Info>
        </div>
      </div>
      <CantiereWork cantiereId={o.cantiere_id} />
      <Diario oid={o.id} />
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <Section label="Avanzamento" testid="client-progress"><Timeline steps={o.timeline} /><ClientPratica oid={o.id} /></Section>
        <div className="space-y-6 min-w-0">
          {appts.length > 0 && (
            <Section label="Prossimi appuntamenti" testid="client-order-appts">
              {appts.map((a) => (
                <div key={a.id} className="rounded-2xl bg-white border border-[#EBEBEB] p-4 mb-2">
                  <p className="font-medium text-sm">{ORDER_APPT_KINDS[a.kind]}</p>
                  <p className="text-sm text-[#757575]">{fmtDay(a.date)} · {a.end_time ? `${a.start_time}–${a.end_time}` : `ore ${a.start_time}`} · {a.location}</p>
                  {a.instructions && <p className="text-xs text-[#757575]">{a.instructions}</p>}
                  {a.client_confirmed_at ? <p className="text-xs text-[#198754] mt-2">Hai confermato il {fmtDateTime(a.client_confirmed_at)}</p>
                    : <Btn className="h-9 mt-2" onClick={() => act(`appointments/${a.id}/confirm`)} data-testid={`client-appt-confirm-${a.id}`}><CalendarCheck className="h-4 w-4" /> Confermo</Btn>}
                </div>
              ))}
            </Section>
          )}
          {project.length > 0 && !o.project_approval && (
            <>
              <Section label="Progetto definitivo"><div className="space-y-2">{project.map((d) => <DocItem key={d.id} d={d} href={`/api/orders/${o.id}/documents/${d.id}/file`} />)}</div></Section>
              <SignBox title="Firma il progetto" text="Confermo il progetto definitivo e ne autorizzo l'esecuzione. Verrà creato un PDF firmato." button="Firmo il progetto" testid="client-project-sign" onSign={(name) => act("project-approval", { name, accept: true })} />
            </>
          )}
          {o.project_approval && <p className="text-sm text-[#198754] flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" /> Progetto firmato da {o.project_approval.by_name} il {fmtDateTime(o.project_approval.at)}</p>}
          {o.status === "consegna_programmata" && !o.client_confirmation && <DeliveryBox act={act} oid={o.id} />}
          {o.client_confirmation && <p className="text-sm text-[#198754] flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" /> Verbale di fine lavori firmato il {fmtDateTime(o.client_confirmation.at)}. Grazie!</p>}
          <Section label="Documenti" testid="client-order-docs">
            <div className="space-y-2">{o.documents.filter((d) => d.tag !== "progetto" && d.tag !== "certificato").map((d) => <DocItem key={d.id} d={d} href={`/api/orders/${o.id}/documents/${d.id}/file`} />)}{!o.documents.length && <p className="text-sm text-[#757575]">Nessun documento.</p>}</div>
          </Section>
          <ClientPercorso oid={o.id} />
          <ClientSopralluogo oid={o.id} />
          <ClientPrima oid={o.id} />
          <ClientLavori oid={o.id} />
          <ClientPostCantiere key={o.client_confirmation?.at || "open"} oid={o.id} />
          {["consegna_programmata", "consegna_completata"].includes(o.status) && !o.client_confirmation && <IssueBox o={o} act={act} />}
          <Section label="Aggiornamenti" testid="client-order-updates">
            {o.events.map((e) => <div key={e.id} className="text-sm border-l-2 border-[#C8BDAB] pl-3 mb-3"><p>{e.public_message || e.summary}</p><p className="text-xs text-[#757575]">{fmtDateTime(e.created_at)}</p></div>)}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default function ClientCommessa() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [extra, setExtra] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    Promise.all([api.get("/client/orders"), api.get("/cantieri-senza-contratto")]).then(([a, b]) => {
      setList(a.data);
      setExtra(b.data);
      setSel(a.data[0]?.id || b.data[0]?.id || null);
    });
  }, []);
  const o = list?.find((x) => x.id === sel);
  const legacy = extra.find((x) => x.id === sel);
  const setO = (data) => setList((l) => l.map((x) => (x.id === data.id ? data : x)));
  if (!list) return <Spinner />;
  const all = [...list, ...extra];
  return (
    <div className="space-y-6">
      <PageHeader label="Cantiere" title="Il tuo cantiere" />
      {!all.length ? <Empty icon={HardHat} title="Nessun cantiere ancora" text="Il cantiere si apre quando accetti un preventivo: da qui seguirai rilievo, progetto, lavori, foto, pagamenti e fine lavori." data-testid="client-orders-empty" /> : (
        <>
          {all.length > 1 && <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="client-cantiere-selector">{all.map((x) => <Chip key={x.id} active={x.id === sel} onClick={() => setSel(x.id)} data-testid={`client-order-chip-${x.id}`}>{x.title}</Chip>)}</div>}
          {o && <OrderView o={o} setO={setO} />}
          {legacy && (
            <CantiereWork key={legacy.id} cantiereId={legacy.id} hero>
              <Btn onClick={() => navigate("/messaggi")} data-testid="cantiere-write-button"><MessageCircle className="h-4 w-4" /> Scrivici</Btn>
            </CantiereWork>
          )}
        </>
      )}
    </div>
  );
}
